import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import { Icon } from '@strata/react-status-icons';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { DeleteFillActivityDialog } from './dialogs/DeleteFillActivityDialog';
import { ViewMetadataDialog } from './dialogs/ViewMetadataDialog';

const initialSortProperties = [{ id: 'eventTimestamp', desc: true }];

const SYSTEM_SOURCE = 'SYSTEM';

const formatDocumentedBy = (value) => {
    if (value === SYSTEM_SOURCE) {
        return <span className="sds_onboarding-activity__system-source">System Integration</span>;
    }
    return value;
};

const formatMissingStatus = (rawStatus) => `No mapping available (${rawStatus})`;

const formatEventStatus = (value, row) => {
    const { metadata } = row.original;
    if (!value) {
        return (
            <span className="sds_onboarding-activity__unmapped-status">
                {formatMissingStatus(metadata.raw_status)}
            </span>
        );
    }
    return value;
};

const getColumns = (setMetadataDialogOpen, setDeleteDialogOpen) => [
    {
        accessorKey: 'eventStatus',
        header: 'Fill Activity',
        size: 200,
        cell: ({ getValue, row }) => <SecondaryData data={formatEventStatus(getValue(), row)} />,
    },
    {
        accessorKey: 'metadata',
        header: () => <Icon icon={IconNames.CODE} />,
        enableSorting: false,
        size: 5,
        cell: ({ getValue, row }) => {
            const { eventStatus, eventTimestamp, rawStatus } = row.original;
            return getValue() ? (
                <Icon
                    icon={IconNames.CODE}
                    intent={Icon.Intent.DEFAULT}
                    tooltipContent="View Metadata"
                    onClick={() =>
                        setMetadataDialogOpen({
                            status: eventStatus,
                            timestamp: eventTimestamp,
                            metadata: {
                                raw_status: rawStatus,
                                ...getValue(),
                            },
                        })
                    }
                />
            ) : null;
        },
    },
    {
        accessorKey: 'eventSource',
        header: 'Documented By',
        size: 150,
        cell: ({ getValue }) => <SecondaryData data={formatDocumentedBy(getValue())} />,
    },
    {
        accessorKey: 'eventTimestamp',
        header: 'Documented On',
        size: 50,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'eventId',
        header: () => <Icon icon={IconNames.TRASH} />,
        enableSorting: false,
        size: 5,
        cell: ({ getValue, row }) => {
            const { eventStatus, rawStatus, eventSource } = row.original;
            return eventSource !== SYSTEM_SOURCE ? (
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.DEFAULT}
                    tooltipContent="Delete Fill Activity"
                    onClick={() =>
                        setDeleteDialogOpen({
                            eventStatus: eventStatus || formatMissingStatus(rawStatus),
                            eventId: getValue(),
                            referralId: row.original.referralId,
                        })
                    }
                />
            ) : null;
        },
    },
];

export const FillActivity = ({ events = undefined, isLoading = true }) => {
    const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    return (
        <>
            <ViewMetadataDialog
                isOpen={!!metadataDialogOpen}
                onClose={() => setMetadataDialogOpen(false)}
                status={metadataDialogOpen?.status}
                timestamp={metadataDialogOpen?.timestamp}
                metadata={metadataDialogOpen?.metadata}
            />
            <DeleteFillActivityDialog
                isOpen={!!deleteDialogOpen}
                eventId={deleteDialogOpen?.eventId}
                referralId={deleteDialogOpen?.referralId}
                eventStatus={deleteDialogOpen?.eventStatus}
                onClose={() => setDeleteDialogOpen(false)}
            />
            <Grid
                data={events}
                isLoading={isLoading}
                sortProperties={initialSortProperties}
                noResults={
                    <div className="padding__around--large">
                        <MessagingBlock
                            size={MessagingBlock.Sizes.SMALL}
                            icon={IconNames.DOCUMENT}
                            primaryText="No fill activities documented"
                        />
                    </div>
                }
                columns={getColumns(setMetadataDialogOpen, setDeleteDialogOpen)}
            />
        </>
    );
};

FillActivity.propTypes = {
    events: PropTypes.arrayOf(
        PropTypes.shape({
            eventId: PropTypes.string,
            eventStatus: PropTypes.string,
            eventTimestamp: PropTypes.string,
            eventSource: PropTypes.string,
            metadata: PropTypes.shape({}),
        }),
    ),
    isLoading: PropTypes.bool,
};
